import React, { useEffect, useState, useMemo } from "react";
import { Card, CardBody, Container, Button, Spinner, Alert, Badge } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api";

/**
 * Round R12 (2026) — Email-link landing page.
 *
 * Handles URLs of the form:
 *   /dms/action/<base64-encoded-token>
 *
 * The token is just `base64("{dmsId}:{approverEmpId}:{approve|deny}")` —
 * unsigned, same format the legacy WinForms /ApproveDMS endpoint accepted.
 * Modern flow:
 *
 *   1. Decode the token client-side. Display the doc ID + action so the
 *      user knows what they're confirming (legacy did this implicitly).
 *
 *   2. Require login: if not authenticated, route protection (existing
 *      AuthProtected) kicks the user to /login. After login they come
 *      back here with the same URL and a valid JWT in session.
 *
 *   3. Verify the JWT empid matches the token's approverEmpId. If not,
 *      show a friendly mismatch error — protects against forwarded
 *      email links being acted on by the wrong user.
 *
 *   4. Show a confirm button. On click → POST /api/dms/{id}/approve
 *      or /deny. Uses the existing R3 endpoints with caller-identity
 *      guards intact.
 *
 *   5. For DENY: requires a comments field (R5's 5-char minimum still
 *      enforced server-side).
 *
 *   6. Show success/failure and offer a navigation back.
 *
 * Security note: the token format is UNSIGNED. Anyone with the token can
 * approve / deny ON THEIR OWN BEHALF (the empid match in step 3 prevents
 * abuse from a forwarded link). For a higher security bar, sign the token
 * with HMAC + expiry — future enhancement, not in this round.
 */
const TokenAction = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const callerEmpId = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Number(u.uid) || 0;
        } catch { return 0; }
    }, []);

    // Round DMS-UAT-8 (2026) — the token is now validated SERVER-SIDE
    // (GET /api/dms/action-token/validate): tokens are HMAC-signed with a
    // 14-day expiry; the server rejects tampered or expired links with a
    // clear message. Legacy unsigned links (already in inboxes) still
    // validate. The old client-side atob() decode is gone — the FE never
    // trusts the token contents itself.
    const [decoded, setDecoded] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const [tokenChecking, setTokenChecking] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTokenChecking(true);
            setTokenError(null);
            try {
                const res = await api.get("dms/action-token/validate", {
                    params: { token: decodeURIComponent(token) },
                });
                if (cancelled) return;
                if (res.data?.valid) {
                    setDecoded({
                        dmsId: Number(res.data.dmsId),
                        approverEmpId: Number(res.data.approverEmpId),
                        action: String(res.data.action || "").toLowerCase(),
                        expiresAt: res.data.expiresAt || null,
                        legacy: Boolean(res.data.legacy),
                    });
                } else {
                    setTokenError(res.data?.error || "This link is not valid.");
                }
            } catch (err) {
                if (cancelled) return;
                setTokenError(
                    err?.response?.data?.error ||
                    "This link is not valid. It may have been altered or expired — open the document from My Pending Approvals instead."
                );
            } finally {
                if (!cancelled) setTokenChecking(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const [denyComments, setDenyComments] = useState("");
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const isApprover = decoded && callerEmpId === decoded.approverEmpId;

    const handleConfirm = async () => {
        if (!decoded) return;
        if (decoded.action === "deny") {
            if (denyComments.trim().length < 5) {
                setError("Please provide a denial reason of at least 5 characters.");
                return;
            }
        }
        setWorking(true);
        setError(null);
        try {
            const body = decoded.action === "deny"
                ? { approverId: decoded.approverEmpId, comments: denyComments.trim() }
                : { approverId: decoded.approverEmpId };
            await api.post(`/dms/${decoded.dmsId}/${decoded.action}`, body);
            setSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.error
                  || err?.response?.data?.message
                  || err?.message
                  || `${decoded.action} failed.`);
        } finally {
            setWorking(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────

    // Round DMS-UAT-8 — while the server verifies the token's signature
    // and expiry, show a spinner instead of a premature error.
    if (tokenChecking) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="text-muted mt-3">Verifying your approval link…</div>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    if (!decoded) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Alert color="danger">
                                <i className="ri-error-warning-line align-bottom me-1"></i>
                                {/* Round DMS-UAT-8 — surface the server's specific
                                    reason (tampered signature vs expired) */}
                                {tokenError || "Invalid or expired action link."} Please open the document directly from
                                <Button color="link" onClick={() => navigate("/dms/my-pending-approvals")}>
                                    My Pending Approvals
                                </Button>
                                .
                            </Alert>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    if (success) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody className="text-center py-5">
                            <i className="ri-checkbox-circle-line text-success" style={{ fontSize: 64 }}></i>
                            <h4 className="mt-3">Document {decoded.action === "approve" ? "approved" : "denied"}</h4>
                            <p className="text-muted">
                                Your decision on document #{decoded.dmsId} has been recorded.
                            </p>
                            <div className="d-flex justify-content-center gap-2 mt-4">
                                <Button color="primary" onClick={() => navigate("/dms/my-pending-approvals")}>
                                    Back to My Pending Approvals
                                </Button>
                                <Button color="light" onClick={() => navigate("/document-management")}>
                                    All Documents
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Card>
                    <CardBody>
                        <h4 className="card-title mb-3">
                            Confirm{" "}
                            <Badge color={decoded.action === "approve" ? "success" : "danger"} pill>
                                {decoded.action.toUpperCase()}
                            </Badge>
                        </h4>
                        <p className="text-muted">
                            You are about to <strong>{decoded.action}</strong> document <strong>#{decoded.dmsId}</strong>.
                            This decision will be recorded in the document's audit trail and emailed to the author and notification list.
                        </p>

                        {/* JWT empid vs token empid mismatch — block the action. */}
                        {!isApprover && (
                            <Alert color="warning" className="mt-3">
                                <i className="ri-alert-line align-bottom me-1"></i>
                                <strong>Signed-in user mismatch.</strong> This action link is for empId{" "}
                                <code>{decoded.approverEmpId}</code>, but you are signed in as{" "}
                                <code>{callerEmpId || "—"}</code>. Sign in as the correct user, or
                                go to{" "}
                                <Button color="link" size="sm" className="p-0 align-baseline" onClick={() => navigate("/dms/my-pending-approvals")}>
                                    My Pending Approvals
                                </Button>{" "}
                                to find this document under your own login.
                            </Alert>
                        )}

                        {decoded.action === "deny" && isApprover && (
                            <div className="mt-3 mb-3">
                                <label className="form-label fw-semibold">
                                    Reason for denial <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    maxLength={255}
                                    value={denyComments}
                                    onChange={(e) => setDenyComments(e.target.value)}
                                    placeholder="Required — minimum 5 characters. Will be shared with the author and notification list."
                                    disabled={working}
                                />
                                <div className="form-text">
                                    {denyComments.length} / 255 characters
                                    {denyComments.length > 0 && denyComments.length < 5 && (
                                        <span className="text-danger ms-2">(at least 5 characters required)</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {error && (
                            <Alert color="danger" className="mt-3">
                                <i className="ri-error-warning-line align-bottom me-1"></i>
                                {error}
                            </Alert>
                        )}

                        <div className="d-flex gap-2 mt-4">
                            <Button
                                color={decoded.action === "approve" ? "success" : "danger"}
                                onClick={handleConfirm}
                                disabled={!isApprover || working}
                            >
                                {working
                                    ? <><Spinner size="sm" /> Working…</>
                                    : `Confirm ${decoded.action}`}
                            </Button>
                            <Button color="light" onClick={() => navigate("/dms/my-pending-approvals")} disabled={working}>
                                Cancel
                            </Button>
                            {isApprover && (
                                <Button
                                    color="link"
                                    onClick={() => navigate(`/document-management?openDmsId=${decoded.dmsId}`)}
                                >
                                    View document first
                                </Button>
                            )}
                        </div>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TokenAction;
